import React, { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, Trash2, Plus, Loader2 } from 'lucide-react';
import { apiGet, apiPost, apiDel } from '../lib/api';
import { toast } from 'sonner';

const SUGGESTIONS = [
  "Which project is most delayed and why?",
  "Show me POs awaiting MD approval",
  "Total open NCRs across projects",
  "Which subcontractor had the highest mandays last week?",
  "Cash position — should I worry about any project?",
];

export default function AskErpChat({ embedded = false }) {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollerRef = useRef(null);

  const loadSessions = async () => {
    try { setSessions(await apiGet('/ask-erp/sessions')); } catch {}
  };
  useEffect(() => { loadSessions(); }, []);

  const loadMessages = async (sid) => {
    if (!sid) { setMessages([]); return; }
    try {
      const r = await apiGet(`/ask-erp/sessions/${sid}/messages`);
      setMessages(r.messages || []);
    } catch {}
  };

  useEffect(() => { loadMessages(activeSession); }, [activeSession]);
  useEffect(() => { if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight; }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');
    const userTurn = { id: 'tmp-' + Date.now(), role: 'user', text: msg, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userTurn]);
    setLoading(true);
    try {
      const r = await apiPost('/ask-erp/chat', { session_id: activeSession, message: msg });
      setActiveSession(r.session_id);
      const assistantTurn = { id: 'tmp-a-' + Date.now(), role: 'assistant', text: r.response, created_at: new Date().toISOString() };
      setMessages(prev => [...prev, assistantTurn]);
      loadSessions();
    } catch (e) {
      toast.error('AI response failed');
      setMessages(prev => prev.filter(m => m.id !== userTurn.id));
    }
    setLoading(false);
  };

  const newChat = () => { setActiveSession(null); setMessages([]); };
  const removeSession = async (sid) => {
    try { await apiDel(`/ask-erp/sessions/${sid}`); }
    catch {}
    if (sid === activeSession) newChat();
    loadSessions();
  };

  return (
    <div className={`flex h-full ${embedded ? '' : 'min-h-[calc(100vh-160px)]'} bg-white border border-slate-200 rounded-sm overflow-hidden`}>
      {/* Sidebar of sessions */}
      <div className="w-60 border-r border-slate-200 flex flex-col bg-slate-50/40">
        <button onClick={newChat} data-testid="ai-new-chat-btn"
          className="m-2 px-3 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-semibold flex items-center gap-1.5 hover:bg-[#2c3658]">
          <Plus size={13} /> New Chat
        </button>
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          <div className="kpi-overline px-1.5 mb-2">Sessions</div>
          {sessions.length === 0 && <div className="text-[11px] text-slate-400 px-1.5">No conversations yet</div>}
          {sessions.map(s => (
            <div key={s.session_id} onClick={() => setActiveSession(s.session_id)} data-testid={`ai-session-${s.session_id}`}
              className={`group px-2 py-2 mb-1 rounded-sm cursor-pointer flex items-start justify-between gap-1 ${activeSession === s.session_id ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-white border border-transparent'}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium truncate">{s.title || 'Untitled chat'}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{(s.updated_at || '').slice(0, 16).replace('T', ' ')}</div>
              </div>
              <button onClick={e => { e.stopPropagation(); removeSession(s.session_id); }}
                className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat surface */}
      <div className="flex-1 flex flex-col min-w-0">
        <div ref={scrollerRef} className="flex-1 overflow-y-auto p-5 space-y-3">
          {messages.length === 0 && !loading && (
            <div className="max-w-2xl mx-auto pt-8">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-9 h-9 rounded-sm bg-gradient-to-br from-[#3b4872] to-[#FF8A65] flex items-center justify-center">
                  <Sparkles size={18} className="text-white" />
                </div>
                <div>
                  <div className="kpi-overline">Ask ERP</div>
                  <h3 className="font-display font-extrabold text-2xl">How can I help today?</h3>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-5">I can answer questions about your projects, POs, NCRs, labour and tasks — using live data.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => send(s)} data-testid={`ai-suggestion-${s.slice(0,20).replace(/\s+/g,'-')}`}
                    className="text-left p-3 bg-white border border-slate-200 rounded-sm hover:border-[#3b4872]/60 hover:bg-indigo-50/30 text-sm transition-all">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <Message key={m.id} role={m.role} text={m.text} />
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Loader2 size={14} className="animate-spin" /> Ask ERP is thinking…
            </div>
          )}
        </div>

        <form onSubmit={e => { e.preventDefault(); send(); }}
          className="border-t border-slate-200 p-3 flex items-end gap-2 bg-white">
          <textarea value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask anything about your projects, POs, NCRs, labour…  (Enter to send · Shift+Enter for newline)"
            rows={1} data-testid="ai-input"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" />
          <button type="submit" disabled={loading || !input.trim()} data-testid="ai-send-btn"
            className="px-4 py-2 bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-40 hover:bg-[#2c3658] flex items-center gap-1.5">
            <Send size={14} /> Send
          </button>
        </form>
      </div>
    </div>
  );
}

function Message({ role, text }) {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-2xl ${isUser ? 'bg-[#3b4872] text-white hover:bg-[#2d3856]' : 'bg-slate-50 border border-slate-200'} px-4 py-2.5 rounded-sm`}>
        {!isUser && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles size={11} className="text-[#3b4872]" />
            <span className="text-[10px] font-display font-bold uppercase tracking-widest text-[#3b4872]">Ask ERP</span>
          </div>
        )}
        <div className="text-sm whitespace-pre-line leading-relaxed">{text}</div>
      </div>
    </div>
  );
}
